import { AxiosResponse } from "axios";
import { appStore, AuthTaskResult } from "src/appStore";
import { objectModelVersionStore } from "./objectModelVersionStore";
import { RequestConfig, rest } from "./rest";

/**
 * Ключ кэша.
 *
 * Браузер различает ответы по URL и по тому, что бэкенд перечислил в Vary.
 * Content-Language в Vary нет (на стенде ответы приходят с `max-age=3600` и
 * либо без Vary, либо с `Vary: Authorization`), поэтому переведённый и базовый
 * ответ на одном URL делят одну запись кэша: PLM просит /objects/{id} с
 * заголовком, Управление - тот же URL без него, и второму достаётся чужой
 * ответ, не доходя до бэкенда. Пока Vary не проставлен на сервере, вариант
 * ответа приходится вносить в ключ самим.
 *
 * В ключ идёт язык ТЕЛА ответа, а не интерфейса. Без перевода тело от языка
 * интерфейса не зависит вовсе, поэтому все языки делят одну запись "base":
 * переключение языка не сбрасывает кэш базовых данных.
 */
const makeVersionKey = (translate: boolean | undefined): string | null => {
  const { version } = objectModelVersionStore;
  const userId =
    appStore.userData.status === "ready" ? appStore.userData.result.id : null;
  if (!version || !userId) return null;
  // новая версия данных зависит от нескольких параметров сразу
  const contentLang = translate ? appStore.curLang : "base";
  return `${version}-${userId}-${contentLang}`;
};

const withObjectVersion = (url: string, translate?: boolean): string => {
  const key = makeVersionKey(translate);
  if (!key) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${encodeURIComponent(key)}`;
};

/**
 * Тот же rest, но с ключом кэша в URL. Заголовками не занимается: их по тому же
 * флагу translate подставляет rest, так что разъехаться они не могут.
 */
export const restWithObjectVersion = {
  get<T = unknown, R extends AuthTaskResult = AxiosResponse<T>, D = unknown>(
    url: string,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return rest.get(withObjectVersion(url, config?.translate), config);
  },

  post<T = unknown, R extends AuthTaskResult = AxiosResponse<T>, D = unknown>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return rest.post(withObjectVersion(url, config?.translate), data, config);
  },

  put<T = unknown, R extends AuthTaskResult = AxiosResponse<T>, D = unknown>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return rest.put(withObjectVersion(url, config?.translate), data, config);
  },

  delete<T = void, R extends AuthTaskResult = AxiosResponse<T>, D = unknown>(
    url: string,
    config?: RequestConfig<D>,
  ): Promise<R> {
    return rest.delete(withObjectVersion(url, config?.translate), config);
  },
};
